import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Image } from 'antd';
import { UserOutlined, LockOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(null);
  const [captchaImage, setCaptchaImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const refreshCaptcha = async () => {
    try {
      const username = form.getFieldValue('username');
      if (username) {
        const response = await axios.post('/api/users/check-captcha/', {
          username
        });
        
        if (response.data.requires_captcha) {
          setCaptchaRequired(true);
          setCaptchaKey(response.data.captcha_key);
          setCaptchaImage(response.data.captcha_image);
          // Reset captcha input field
          form.setFieldsValue({ captcha: '' });
        }
      }
    } catch (error) {
      console.error('Error refreshing captcha:', error);
      message.error('刷新验证码失败，请重试');
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // Check if captcha is required before attempting login
      if (!captchaRequired) {
        await refreshCaptcha();
        if (captchaRequired) {
          setLoading(false);
          return;
        }
      }

      const loginData = {
        username: values.username,
        password: values.password
      };

      if (captchaRequired) {
        if (!values.captcha) {
          message.error('请输入验证码');
          setLoading(false);
          return;
        }
        loginData.captcha_key = captchaKey;
        loginData.captcha_value = values.captcha;
      }

      const response = await axios.post('/api/users/login/', loginData);

      if (response.data.token) {
        // Store the token
        localStorage.setItem('token', response.data.token.access);
        localStorage.setItem('refreshToken', response.data.token.refresh);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        // Configure axios defaults for future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token.access}`;

        message.success('登录成功！');

        // Redirect based on user role
        if (response.data.user.role === 'BOSS' || response.data.user.role === 'MANAGER') {
          navigate('/admin/orders');
        } else if (response.data.user.role === 'STAFF') {
          navigate('/staff/orders');
        } else if (response.data.user.role === 'PRINTER') {
          navigate('/printer/orders');
        } else {
          navigate('/my-orders');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      // Display the specific error message from the backend
      if (error.response?.data) {
        if (error.response.data.captcha_required) {
          setCaptchaRequired(true);
          await refreshCaptcha();
        }
        const errorMessage = error.response.data.message || 
                           error.response.data.non_field_errors?.[0] || 
                           error.response.data.detail || 
                           '登录失败，请重试！';
        message.error(errorMessage);
      } else {
        message.error('登录失败，请重试！');
      }
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Card title="登录" className="login-card" headStyle={{ position: 'relative' }}>
        <div className="register-corner">
          <Link to="/register">
            <div className="corner-fold"></div>
            <span className="register-text">注册</span>
          </Link>
        </div>
        <Form
          form={form}
          name="login"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: '请输入用户名！' }]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="用户名" 
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码！' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
            />
          </Form.Item>

          {captchaRequired && (
            <Form.Item
              name="captcha"
              rules={[{ required: true, message: '请输入验证码！' }]}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Input 
                  placeholder="验证码" 
                  style={{ width: '120px' }}
                />
                {captchaImage && (
                  <Image
                    src={captchaImage}
                    preview={false}
                    style={{ height: '32px' }}
                    onError={(e) => {
                      console.error('Captcha image load error:', e);
                      message.error('验证码加载失败，请刷新重试');
                    }}
                  />
                )}
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={refreshCaptcha}
                  type="link"
                />
              </div>
            </Form.Item>
          )}

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              className="login-button"
              loading={loading}
            >
              登录
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Login;
